import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { isAxiosError } from "axios";
import {
  CheckCircle2,
  ClipboardList,
  Edit3,
  FileClock,
  Loader2,
  Plus,
  RefreshCw,
  Save,
  Trash2,
  X,
} from "lucide-react";
import { CalidadControlModificacionDeleteModal } from "../../../components/control-modificaciones/CalidadControlModificacionDeleteModal";
import { CalidadControlModificacionFormModal } from "../../../components/control-modificaciones/CalidadControlModificacionFormModal";
import { CalidadControlModificacionesTable } from "../../../components/control-modificaciones/CalidadControlModificacionesTable";
import { Iso900114001ConfirmDialog } from "../../../components/shared/Iso900114001ConfirmDialog";
import { Iso900114001OverviewCard } from "../../../components/shared/Iso900114001OverviewCard";
import { Iso900114001SectionHeader } from "../../../components/shared/Iso900114001SectionHeader";
import { Iso900114001Tabs } from "../../../components/shared/Iso900114001Tabs";
import type {
  CalidadControlModificacion,
  CalidadControlModificacionPayload,
} from "../../../types/control-modificaciones";
import {
  createCalidadPlanificacionCambioControlModificacion,
  createCalidadPlanificacionDeCambio,
  deleteCalidadPlanificacionCambioControlModificacion,
  deleteCalidadPlanificacionDeCambio,
  updateCalidadPlanificacionCambioControlModificacion,
  updateCalidadPlanificacionDeCambio,
} from "../api/planificacion-de-cambios";
import { useCalidadPlanificacionDeCambios } from "../hooks/useCalidadPlanificacionDeCambios";
import {
  createEmptyPlanificacionCambioForm,
  defaultPlanificacionCambiosFilters,
  mapPlanificacionCambioToPayload,
  type CalidadPlanificacionDeCambio,
  type CalidadPlanificacionDeCambioPayload,
  type CalidadPlanificacionDeCambiosFilters,
} from "../types/planificacion-de-cambios";

type Props = {
  onBack: () => void;
};

type RecordFormState =
  | { open: false }
  | { open: true; mode: "create" | "edit"; row: CalidadPlanificacionDeCambio | null };

type DeleteState =
  | { open: false }
  | { open: true; row: CalidadPlanificacionDeCambio };

type ControlFormState =
  | { open: false }
  | { open: true; mode: "create" | "edit"; row: CalidadControlModificacion | null };

type ControlDeleteState =
  | { open: false }
  | { open: true; row: CalidadControlModificacion };

export function CalidadPlanificacionCambiosSection({ onBack }: Props) {
  const [activeTab, setActiveTab] = useState<"cambios" | "control">("cambios");
  const [filters, setFilters] = useState<CalidadPlanificacionDeCambiosFilters>(
    defaultPlanificacionCambiosFilters,
  );
  const { rows, controlModificaciones, meta, loading, error, reload } =
    useCalidadPlanificacionDeCambios(filters);
  const [formState, setFormState] = useState<RecordFormState>({ open: false });
  const [deleteState, setDeleteState] = useState<DeleteState>({ open: false });
  const [controlFormState, setControlFormState] = useState<ControlFormState>({ open: false });
  const [controlDeleteState, setControlDeleteState] = useState<ControlDeleteState>({ open: false });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [controlError, setControlError] = useState<string | null>(null);

  const realizedCount = useMemo(
    () => rows.filter((row) => row.estado?.trim().toUpperCase() === "REALIZADO").length,
    [rows],
  );

  const openCreate = () => {
    setFormError(null);
    setFormState({ open: true, mode: "create", row: null });
  };

  const openEdit = (row: CalidadPlanificacionDeCambio) => {
    setFormError(null);
    setFormState({ open: true, mode: "edit", row });
  };

  const handleSubmit = async (payload: CalidadPlanificacionDeCambioPayload) => {
    if (!formState.open) return;

    setSubmitting(true);
    setFormError(null);

    try {
      if (formState.mode === "edit" && formState.row) {
        await updateCalidadPlanificacionDeCambio(formState.row.id, payload);
      } else {
        await createCalidadPlanificacionDeCambio(payload);
      }

      setFormState({ open: false });
      await reload();
    } catch (requestError) {
      setFormError(
        getRequestErrorMessage(requestError, "No se pudo guardar la planificación de cambios."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteState.open) return;

    setSubmitting(true);

    try {
      await deleteCalidadPlanificacionDeCambio(deleteState.row.id);
      setDeleteState({ open: false });
      await reload();
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitControl = async (payload: CalidadControlModificacionPayload) => {
    if (!controlFormState.open) return;

    setSubmitting(true);
    setControlError(null);

    try {
      if (controlFormState.mode === "edit" && controlFormState.row) {
        await updateCalidadPlanificacionCambioControlModificacion(
          controlFormState.row.id,
          payload,
        );
      } else {
        await createCalidadPlanificacionCambioControlModificacion(payload);
      }

      setControlFormState({ open: false });
      await reload();
    } catch (requestError) {
      setControlError(
        getRequestErrorMessage(requestError, "No se pudo guardar el control documental."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteControl = async () => {
    if (!controlDeleteState.open) return;

    setSubmitting(true);

    try {
      await deleteCalidadPlanificacionCambioControlModificacion(controlDeleteState.row.id);
      setControlDeleteState({ open: false });
      await reload();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="calidad-section planificacion-section">
      <Iso900114001SectionHeader
        eyebrow="Gestión del cambio"
        title="Planificación de cambios"
        description="Tabla editable de planificación de cambios y control documental."
        onBack={onBack}
        onRefresh={() => {
          void reload();
        }}
        refreshing={loading}
      />

      <div className="iso9001-overview-grid">
        <Iso900114001OverviewCard
          icon={ClipboardList}
          label="Cambios"
          value={loading ? "..." : meta.total}
        />
        <Iso900114001OverviewCard
          icon={CheckCircle2}
          label="Realizados"
          value={loading ? "..." : realizedCount}
        />
        <Iso900114001OverviewCard
          icon={FileClock}
          label="Control documental"
          value={loading ? "..." : controlModificaciones.length}
        />
      </div>

      <Iso900114001Tabs
        tabs={[
          { id: "cambios", label: "Cambios registrados", count: meta.total },
          {
            id: "control",
            label: "Control documental",
            count: controlModificaciones.length,
          },
        ]}
        activeTab={activeTab}
        onChange={setActiveTab}
        ariaLabel="Tablas de planificación de cambios"
      />

      {activeTab === "cambios" ? (
        <section className="planificacion-panel">
        <div className="planificacion-toolbar">
          <div>
            <div className="calidad-toolbar-eyebrow">Tabla principal</div>
            <h3 className="calidad-toolbar-title">Cambios registrados</h3>
          </div>
          <div className="calidad-toolbar-meta">
            <span className="calidad-toolbar-count">
              {loading ? "Cargando..." : `${meta.total} registros`}
            </span>
            <button type="button" onClick={openCreate} className="calidad-primary-button">
              <Plus size={16} />
              Nuevo registro
            </button>
          </div>
        </div>

        <div className="planificacion-filters">
          <label className="planificacion-filter-field planificacion-filter-search">
            <span>Buscar</span>
            <input
              value={filters.search}
              onChange={(event) =>
                setFilters((current) => ({ ...current, search: event.target.value }))
              }
              placeholder="Input, acciones, responsable, recursos o seguimiento"
            />
          </label>

          <label className="planificacion-filter-field">
            <span>Estado</span>
            <select
              value={filters.estado}
              onChange={(event) =>
                setFilters((current) => ({ ...current, estado: event.target.value }))
              }
            >
              <option value="">Todos</option>
              {meta.estados.map((estado) => (
                <option key={estado} value={estado}>
                  {estado}
                </option>
              ))}
            </select>
          </label>

          <label className="planificacion-filter-field">
            <span>Desde</span>
            <input
              type="date"
              value={filters.fecha_desde}
              onChange={(event) =>
                setFilters((current) => ({ ...current, fecha_desde: event.target.value }))
              }
            />
          </label>

          <label className="planificacion-filter-field">
            <span>Hasta</span>
            <input
              type="date"
              value={filters.fecha_hasta}
              onChange={(event) =>
                setFilters((current) => ({ ...current, fecha_hasta: event.target.value }))
              }
            />
          </label>

          <button
            type="button"
            onClick={() => setFilters(defaultPlanificacionCambiosFilters)}
            className="calidad-toolbar-reset"
          >
            Limpiar filtros
          </button>
        </div>

        {error ? (
          <Feedback title="No se pudo cargar la tabla" copy={error} onRetry={() => void reload()} />
        ) : loading ? (
          <LoadingState title="Cargando planificación" />
        ) : rows.length === 0 ? (
          <div className="calidad-feedback-state">
            <div className="calidad-feedback-title">No hay cambios registrados</div>
            <p className="calidad-feedback-copy">
              Crea el primer registro o limpia los filtros activos.
            </p>
            <button type="button" onClick={openCreate} className="calidad-primary-button">
              <Plus size={16} />
              Nuevo registro
            </button>
          </div>
        ) : (
          <div className="planificacion-table-scroll">
            <table className="calidad-table planificacion-table min-w-full">
              <thead>
                <tr>
                  <th className="planificacion-actions-col">Acciones</th>
                  <th className="planificacion-date-col">Fecha planificación</th>
                  <th className="planificacion-input-col">Input (consecuencias al sistema)</th>
                  <th className="planificacion-actions-text-col">Acciones</th>
                  <th className="planificacion-responsable-col">Responsable</th>
                  <th className="planificacion-recursos-col">Recursos</th>
                  <th className="planificacion-date-col">Fecha prevista</th>
                  <th className="planificacion-status-col">Estado</th>
                  <th className="planificacion-seguimiento-col">Acciones realizadas / Seguimiento</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="planificacion-actions-col">
                      <div className="calidad-table-actions-row">
                        <button
                          type="button"
                          onClick={() => openEdit(row)}
                          className="calidad-icon-button"
                          title="Editar registro"
                          aria-label="Editar registro"
                        >
                          <Edit3 size={15} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteState({ open: true, row })}
                          className="calidad-icon-button danger"
                          title="Eliminar registro"
                          aria-label="Eliminar registro"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                    <td className="planificacion-date-col">{formatDisplayDate(row.fecha_planificacion)}</td>
                    <td className="planificacion-input-col">
                      <PlanificacionScrollableCell value={row.input_consecuencias_sistema} />
                    </td>
                    <td className="planificacion-actions-text-col">
                      <PlanificacionScrollableCell value={row.acciones} />
                    </td>
                    <td className="planificacion-responsable-col">
                      <PlanificacionScrollableCell value={row.responsable} />
                    </td>
                    <td className="planificacion-recursos-col">
                      <PlanificacionScrollableCell value={row.recursos} />
                    </td>
                    <td className="planificacion-date-col">{row.fecha_prevista || "—"}</td>
                    <td className="planificacion-status-col">
                      <span
                        className={
                          row.estado?.trim().toUpperCase() === "REALIZADO"
                            ? "planificacion-status realizado"
                            : "planificacion-status"
                        }
                        title={row.estado || undefined}
                      >
                        {row.estado || "—"}
                      </span>
                    </td>
                    <td className="planificacion-seguimiento-col">
                      <PlanificacionScrollableCell value={row.acciones_realizadas_seguimiento} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </section>
      ) : (
        <CalidadControlModificacionesTable
          rows={controlModificaciones}
          loading={loading}
          error={error}
          onRetry={() => void reload()}
          onCreate={() => {
            setControlError(null);
            setControlFormState({ open: true, mode: "create", row: null });
          }}
          onEdit={(row) => {
            setControlError(null);
            setControlFormState({ open: true, mode: "edit", row });
          }}
          onDelete={(row) => setControlDeleteState({ open: true, row })}
        />
      )}

      <PlanificacionCambioFormModal
        state={formState}
        submitting={submitting}
        error={formError}
        onClose={() => setFormState({ open: false })}
        onSubmit={(payload) => void handleSubmit(payload)}
      />

      <Iso900114001ConfirmDialog
        open={deleteState.open}
        title="Eliminar registro"
        description="Se eliminará el registro de planificación seleccionado. Esta acción no se puede deshacer."
        confirming={submitting}
        onClose={() => setDeleteState({ open: false })}
        onConfirm={() => void handleDelete()}
      />

      <CalidadControlModificacionFormModal
        open={controlFormState.open}
        mode={controlFormState.open ? controlFormState.mode : "create"}
        initialValues={controlFormState.open ? controlFormState.row : null}
        submitting={submitting}
        error={controlError}
        onClose={() => setControlFormState({ open: false })}
        onSubmit={(payload) => void handleSubmitControl(payload)}
      />

      <CalidadControlModificacionDeleteModal
        open={controlDeleteState.open}
        title="Eliminar modificación"
        description={
          controlDeleteState.open
            ? `Se eliminará la modificación ${controlDeleteState.row.fecha}.`
            : ""
        }
        deleting={submitting}
        onClose={() => setControlDeleteState({ open: false })}
        onConfirm={() => void handleDeleteControl()}
      />
    </section>
  );
}


function PlanificacionScrollableCell({ value }: { value: string | null }) {
  const content = value?.trim();

  return (
    <div className="planificacion-cell-scroll" title={content || undefined}>
      {content || "—"}
    </div>
  );
}

function PlanificacionCambioFormModal({
  state,
  submitting,
  error,
  onClose,
  onSubmit,
}: {
  state: RecordFormState;
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (payload: CalidadPlanificacionDeCambioPayload) => void;
}) {
  const [values, setValues] = useState<CalidadPlanificacionDeCambioPayload>(
    state.open && state.mode === "edit" && state.row
      ? mapPlanificacionCambioToPayload(state.row)
      : createEmptyPlanificacionCambioForm(),
  );

  useEffect(() => {
    if (!state.open) return;
    setValues(
      state.mode === "edit" && state.row
        ? mapPlanificacionCambioToPayload(state.row)
        : createEmptyPlanificacionCambioForm(),
    );
  }, [state]);

  if (!state.open) return null;

  const setPatch = (patch: Partial<CalidadPlanificacionDeCambioPayload>) =>
    setValues((current) => ({ ...current, ...patch }));

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit({
      fecha_planificacion: values.fecha_planificacion || null,
      input_consecuencias_sistema: normalizeFormString(values.input_consecuencias_sistema),
      acciones: normalizeFormString(values.acciones),
      responsable: normalizeFormString(values.responsable),
      recursos: normalizeFormString(values.recursos),
      fecha_prevista: normalizeFormString(values.fecha_prevista),
      estado: normalizeFormString(values.estado),
      acciones_realizadas_seguimiento: normalizeFormString(values.acciones_realizadas_seguimiento),
    });
  };

  return (
    <div className="calidad-modal-backdrop" role="presentation">
      <form className="calidad-modal planificacion-modal" onSubmit={handleSubmit}>
        <div className="planificacion-modal-header">
          <div>
            <div className="calidad-section-eyebrow">
              {state.mode === "edit" ? "Editar registro" : "Nuevo registro"}
            </div>
            <h3 className="planificacion-modal-title">Planificación de cambios</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="planificacion-modal-close"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        {error ? <div className="planificacion-inline-error">{error}</div> : null}

        <div className="planificacion-form-grid">
          <label className="planificacion-field">
            <span>Fecha planificación</span>
            <input
              type="date"
              value={values.fecha_planificacion ?? ""}
              onChange={(event) => setPatch({ fecha_planificacion: event.target.value || null })}
            />
          </label>

          <label className="planificacion-field">
            <span>Fecha prevista</span>
            <input
              value={values.fecha_prevista ?? ""}
              onChange={(event) => setPatch({ fecha_prevista: event.target.value })}
              placeholder="Ej. Septiembre, fin de año, 2026-12-31"
            />
          </label>

          <label className="planificacion-field">
            <span>Estado</span>
            <input
              value={values.estado ?? ""}
              onChange={(event) => setPatch({ estado: event.target.value })}
              placeholder="Ej. Realizado, pendiente"
            />
          </label>

          <label className="planificacion-field planificacion-field-span-3">
            <span>Input (consecuencias al sistema)</span>
            <textarea
              value={values.input_consecuencias_sistema ?? ""}
              onChange={(event) => setPatch({ input_consecuencias_sistema: event.target.value })}
              rows={3}
            />
          </label>

          <label className="planificacion-field planificacion-field-span-3">
            <span>Acciones</span>
            <textarea
              value={values.acciones ?? ""}
              onChange={(event) => setPatch({ acciones: event.target.value })}
              rows={4}
            />
          </label>

          <label className="planificacion-field">
            <span>Responsable</span>
            <textarea
              value={values.responsable ?? ""}
              onChange={(event) => setPatch({ responsable: event.target.value })}
              rows={3}
            />
          </label>

          <label className="planificacion-field planificacion-field-span-2">
            <span>Recursos</span>
            <textarea
              value={values.recursos ?? ""}
              onChange={(event) => setPatch({ recursos: event.target.value })}
              rows={3}
            />
          </label>

          <label className="planificacion-field planificacion-field-span-3">
            <span>Acciones realizadas / Seguimiento</span>
            <textarea
              value={values.acciones_realizadas_seguimiento ?? ""}
              onChange={(event) =>
                setPatch({ acciones_realizadas_seguimiento: event.target.value })
              }
              rows={5}
            />
          </label>
        </div>

        <div className="planificacion-modal-actions">
          <button type="button" onClick={onClose} disabled={submitting} className="calidad-back-button">
            Cancelar
          </button>
          <button type="submit" disabled={submitting} className="calidad-primary-button">
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Guardar
          </button>
        </div>
      </form>
    </div>
  );
}

function Feedback({ title, copy, onRetry }: { title: string; copy: string; onRetry: () => void }) {
  return (
    <div className="calidad-feedback-state">
      <div className="calidad-feedback-title">{title}</div>
      <p className="calidad-feedback-copy">{copy}</p>
      <button type="button" onClick={onRetry} className="calidad-feedback-action">
        <RefreshCw size={15} />
        Reintentar
      </button>
    </div>
  );
}

function LoadingState({ title }: { title: string }) {
  return (
    <div className="calidad-feedback-state">
      <Loader2 size={20} className="animate-spin text-[#0b4a2f]" />
      <div className="calidad-feedback-title">{title}</div>
    </div>
  );
}

function formatDisplayDate(value: string | null) {
  if (!value) return "—";
  return new Date(`${value}T00:00:00`).toLocaleDateString();
}

function normalizeFormString(value: string | null) {
  const normalized = (value ?? "").trim();
  return normalized === "" ? null : normalized;
}

function getRequestErrorMessage(error: unknown, fallback: string) {
  if (isAxiosError(error)) {
    const message = error.response?.data?.message;
    if (typeof message === "string" && message.trim() !== "") {
      return message;
    }
  }

  return fallback;
}
